/**
 * Drops presentational markup the model writes into its answers.
 *
 * `rehype-raw` lets raw HTML from the answer through, and with it whatever the
 * model decided to style itself: `style="font-family: …"`, `<font face size>`,
 * `<center>`. That is why one answer suddenly looked different from the next.
 * Typography belongs to the answer surface (`ChatMessage.module.sass`), so this
 * step runs after `rehype-raw` and removes those overrides. Structure, classes
 * and `data-*` attributes stay — `inline-resource` and the citation spans
 * depend on them.
 *
 * Walks the tree itself, like `rehypeDocumentCitations`, because
 * `unist-util-visit` is only installed transitively.
 */

type HastNode = {
    type: string;
    tagName?: string;
    properties?: Record<string, unknown>;
    children?: HastNode[];
};

/** Presentational attributes as hast names them (`bgcolor` → `bgColor`). */
const PRESENTATIONAL_PROPERTIES = ['style', 'face', 'size', 'color', 'bgColor', 'align'];

/** Legacy tags that only style; they become their neutral counterpart. */
const NEUTRAL_TAG: Record<string, string> = {
    font: 'span',
    big: 'span',
    small: 'span',
    center: 'div',
};

export const rehypeStripModelStyling = () => (tree: HastNode) => {
    const walk = (node: HastNode) => {
        if (node.type === 'element') {
            const neutral = NEUTRAL_TAG[String(node.tagName)];
            if (neutral) node.tagName = neutral;
            if (node.properties) {
                for (const name of PRESENTATIONAL_PROPERTIES) delete node.properties[name];
            }
        }
        node.children?.forEach(walk);
    };
    walk(tree);
};
